import { useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {
  Truck,
  ShieldCheck,
  Star,
  ArrowRight,
  MapPin,
  Package,
  Boxes,
  Clock,
  Quote,
  Sparkles,
} from "lucide-react";
import QuoteWizard from "@/components/QuoteWizard";
import { POPULAR_ROUTES, STATES_BY_SLUG } from "@/data/states";

const HERO_IMG =
  "https://static.prod-images.emergentagent.com/jobs/3f072a24-1db2-46b9-ac47-79739bdb7ae8/images/83e2ae6a04f5a2d55032d75a7fe27e98412c32827addb68b4fe28fdd5a9e8937.png";
const WAVE_IMG =
  "https://static.prod-images.emergentagent.com/jobs/3f072a24-1db2-46b9-ac47-79739bdb7ae8/images/efba610b00867f000afe22875bd22bec2fcaebe9169b56252b63336f9293df3d.png";
const PACKING_IMG =
  "https://static.prod-images.emergentagent.com/jobs/3f072a24-1db2-46b9-ac47-79739bdb7ae8/images/a5e5dd1d2001526a6da975a7221d2a4764d450e2740390f707da230998c6847e.png";
const TRUCK_IMG =
  "https://images.unsplash.com/photo-1616432043562-3671ea2e5242?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NjZ8MHwxfHNlYXJjaHwxfHxtb3ZpbmclMjB0cnVjayUyMGhpZ2h3YXl8ZW58MHx8fHwxNzc5OTIzOTg0fDA&ixlib=rb-4.1.0&q=85";
const COUPLE_IMG =
  "https://images.pexels.com/photos/8962864/pexels-photo-8962864.jpeg";

export default function Landing() {
  const [wizardOpen, setWizardOpen] = useState(false);

  return (
    <div className="min-h-screen bg-movewave-paper text-movewave-ink overflow-x-hidden">
      <Header onOpenWizard={() => setWizardOpen(true)} />
      <Hero onOpenWizard={() => setWizardOpen(true)} />
      <Marquee />
      <PopularRoutes onOpenWizard={() => setWizardOpen(true)} />
      <HowItWorks />
      <Services />
      <SocialProof />
      <Testimonials />
      <FAQ />
      <FinalCTA onOpenWizard={() => setWizardOpen(true)} />
      <Footer />
      <QuoteWizard open={wizardOpen} onOpenChange={setWizardOpen} />
    </div>
  );
}

/* ============== Header ============== */
function Header({ onOpenWizard }) {
  return (
    <header className="sticky top-0 z-40 bg-white/85 backdrop-blur-xl border-b-2 border-movewave-ink">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2" data-testid="brand-logo">
          <div className="w-8 h-8 bg-movewave-blue flex items-center justify-center">
            <Truck className="w-4 h-4 text-white" strokeWidth={2.5} />
          </div>
          <span className="font-display font-black text-xl tracking-tight">
            VanLines <span className="text-movewave-blue">Scout</span>
          </span>
        </Link>
        <nav className="hidden md:flex items-center gap-8 text-sm font-bold uppercase tracking-wider">
          <a href="#how" className="hover:text-movewave-blue">How it works</a>
          <a href="#services" className="hover:text-movewave-blue">Services</a>
          <Link to="/movers" className="hover:text-movewave-blue" data-testid="nav-movers">By state</Link>
          <a href="#reviews" className="hover:text-movewave-blue">Reviews</a>
          <a href="#faq" className="hover:text-movewave-blue">FAQ</a>
        </nav>
        <Button
          onClick={onOpenWizard}
          className="bg-movewave-blue hover:bg-movewave-blue-hover text-white font-bold uppercase tracking-wider rounded-none h-10 px-5 text-xs"
          data-testid="header-cta-btn"
        >
          Get free quote
        </Button>
      </div>
    </header>
  );
}

/* ============== Hero ============== */
function Hero({ onOpenWizard }) {
  return (
    <section className="relative">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-14 pb-20 sm:pt-20 sm:pb-28 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-12 items-center">
        {/* Left: copy */}
        <div className="lg:col-span-7">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="inline-flex items-center gap-2 bg-movewave-blue text-white px-3 py-1.5 text-xs font-bold uppercase tracking-[0.2em] mb-6"
          >
            <Sparkles className="w-3.5 h-3.5 text-movewave-orange" />
            East Coast specialists · Northeast to Southeast
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 32 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.05 }}
            className="font-display font-black text-5xl sm:text-6xl lg:text-7xl leading-[0.95] tracking-tighter"
          >
            Moving south?<br />
            <span className="text-movewave-blue">Match faster.</span><br />
            Pay less.
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.15 }}
            className="mt-6 text-lg text-gray-700 max-w-xl leading-relaxed"
          >
            We specialize in <strong>Northeast → Southeast</strong> moves —
            NY, NJ, DE, PA and the rest of the corridor heading to NC, SC, GA, FL.
            Compare up to 4 vetted, licensed movers in 60 seconds. Free, no obligation.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.25 }}
            className="mt-8 flex flex-col sm:flex-row gap-4"
          >
            <Button
              onClick={onOpenWizard}
              className="bg-movewave-orange hover:bg-movewave-orange-hover text-white font-bold uppercase tracking-wider rounded-none h-14 px-8 text-sm btn-brut"
              data-testid="hero-cta-btn"
            >
              Get my free quote
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
            <a
              href="#how"
              className="inline-flex items-center justify-center h-14 px-8 border-2 border-movewave-ink font-bold uppercase tracking-wider text-sm hover:bg-movewave-ink hover:text-white transition-colors"
            >
              See how it works
            </a>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            className="mt-10 grid grid-cols-3 gap-6 max-w-md"
          >
            <Stat n="60s" l="Quote time" />
            <Stat n="4.9★" l="Avg rating" />
            <Stat n="$0" l="To compare" />
          </motion.div>
        </div>

        {/* Right: visual */}
        <div className="lg:col-span-5 relative">
          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.7 }}
            className="relative aspect-[4/5] border-2 border-movewave-ink overflow-hidden bg-white shadow-[12px_12px_0_#002FA7]"
          >
            <img
              src={HERO_IMG}
              alt="Pristine cardboard moving boxes in sunlit empty room"
              className="absolute inset-0 w-full h-full object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent" />
            <div className="absolute bottom-4 left-4 right-4 bg-movewave-ink text-white p-4 flex items-center gap-3">
              <ShieldCheck className="w-6 h-6 text-movewave-orange flex-shrink-0" />
              <div>
                <div className="text-xs uppercase tracking-wider font-bold opacity-70">Vetted & insured</div>
                <div className="text-sm font-bold">Every partner — licensed, bonded, reviewed.</div>
              </div>
            </div>
          </motion.div>
          {/* Floating wave accent */}
          <img
            src={WAVE_IMG}
            alt=""
            aria-hidden
            className="hidden lg:block absolute -bottom-12 -left-10 w-40 opacity-80 pointer-events-none"
          />
        </div>
      </div>
    </section>
  );
}

function Stat({ n, l }) {
  return (
    <div>
      <div className="font-display font-black text-3xl tracking-tight">{n}</div>
      <div className="text-xs uppercase tracking-wider text-gray-500 mt-1">{l}</div>
    </div>
  );
}

/* ============== Marquee / partners ============== */
function Marquee() {
  const items = [
    "ALLIED · ",
    "MAYFLOWER · ",
    "NORTH AMERICAN · ",
    "ATLAS · ",
    "U-PACK · ",
    "TWO MEN · ",
    "PODS · ",
    "BEKINS · ",
  ];
  const doubled = [...items, ...items, ...items, ...items];
  return (
    <div className="bg-movewave-ink text-white border-y-2 border-movewave-ink overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex items-center gap-8">
        <span className="text-xs uppercase tracking-[0.2em] font-bold text-movewave-orange whitespace-nowrap">
          East Coast network
        </span>
        <div className="flex-1 overflow-hidden">
          <div className="marquee-track animate-marquee flex gap-8 text-sm font-bold uppercase tracking-[0.15em] text-white/60">
            {doubled.map((t, i) => (
              <span key={i} className="whitespace-nowrap">{t}</span>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ============== Popular routes ============== */
function PopularRoutes({ onOpenWizard }) {
  return (
    <section id="routes" className="py-20 sm:py-24 bg-white border-b-2 border-movewave-ink">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-6 mb-10">
          <div>
            <div className="text-xs uppercase tracking-[0.2em] font-bold text-movewave-blue mb-3">
              Popular routes
            </div>
            <h2 className="font-display font-black text-4xl sm:text-5xl tracking-tighter leading-none">
              Northeast.<br />
              <span className="text-movewave-blue">To Southeast.</span>
            </h2>
            <p className="mt-4 text-gray-600 max-w-xl">
              We've moved thousands of families and retirees along the Atlantic corridor. Pick your route — or just hit the wizard.
            </p>
          </div>
          <Link to="/movers" className="text-sm font-bold uppercase tracking-wider text-movewave-blue hover:underline whitespace-nowrap">
            All East Coast states →
          </Link>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
          {POPULAR_ROUTES.map((r) => {
            const from = STATES_BY_SLUG[r.from];
            const to = STATES_BY_SLUG[r.to];
            return (
              <button
                key={`${r.from}-${r.to}`}
                onClick={onOpenWizard}
                className="text-left border-2 border-movewave-ink p-5 bg-white hover:bg-movewave-blue hover:text-white transition-colors group"
                data-testid={`route-${r.from}-${r.to}`}
              >
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <div className="text-xs uppercase tracking-wider font-bold opacity-60 group-hover:opacity-90 mb-1">
                      {from?.code} → {to?.code}
                    </div>
                    <div className="font-display font-bold text-lg tracking-tight">
                      {r.label}
                    </div>
                  </div>
                  <ArrowRight className="w-5 h-5 flex-shrink-0 group-hover:translate-x-1 transition-transform" />
                </div>
              </button>
            );
          })}
        </div>
      </div>
    </section>
  );
}

/* ============== How it works ============== */
function HowItWorks() {
  const steps = [
    {
      n: "01",
      t: "Tell us about your move",
      d: "Answer 6 quick questions — type of move, ZIP codes, date, home size.",
      icon: <MapPin className="w-5 h-5" />,
    },
    {
      n: "02",
      t: "Get matched with movers",
      d: "We connect you with up to 4 vetted, licensed companies in your area.",
      icon: <Truck className="w-5 h-5" />,
    },
    {
      n: "03",
      t: "Compare and book",
      d: "Review free quotes, compare reviews, and book the mover that fits your budget.",
      icon: <ShieldCheck className="w-5 h-5" />,
    },
  ];
  return (
    <section id="how" className="bg-white border-y-2 border-movewave-ink py-20 sm:py-28">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-2xl mb-14">
          <div className="text-xs uppercase tracking-[0.2em] font-bold text-movewave-blue mb-3">
            How it works
          </div>
          <h2 className="font-display font-black text-4xl sm:text-5xl tracking-tighter leading-none">
            Three steps.<br />Sixty seconds.
          </h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {steps.map((s) => (
            <div
              key={s.n}
              className="border-2 border-movewave-ink p-8 bg-white relative hover:shadow-[8px_8px_0_#002FA7] transition-all"
              data-testid={`how-step-${s.n}`}
            >
              <div className="font-display font-black text-7xl text-movewave-blue/10 leading-none absolute top-4 right-4 select-none">
                {s.n}
              </div>
              <div className="w-10 h-10 bg-movewave-ink text-white flex items-center justify-center mb-5">
                {s.icon}
              </div>
              <h3 className="font-display font-bold text-2xl mb-2 tracking-tight">{s.t}</h3>
              <p className="text-gray-600 leading-relaxed">{s.d}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ============== Services bento ============== */
function Services() {
  return (
    <section id="services" className="py-20 sm:py-28">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-2xl mb-14">
          <div className="text-xs uppercase tracking-[0.2em] font-bold text-movewave-blue mb-3">
            Services
          </div>
          <h2 className="font-display font-black text-4xl sm:text-5xl tracking-tighter leading-none">
            Whatever you're moving — we've matched it.
          </h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <ServiceCard
            icon={<MapPin className="w-6 h-6" />}
            title="Local moves"
            desc="Same city, same day. Hourly crews from 2–4 movers."
            className="md:col-span-2 md:row-span-1"
            bg={null}
          />
          <ServiceCard
            icon={<Truck className="w-6 h-6" />}
            title="Long distance"
            desc="Cross-state and coast-to-coast, with tracking and delivery windows."
            className="bg-movewave-blue text-white border-movewave-blue"
            image={TRUCK_IMG}
          />
          <ServiceCard
            icon={<Package className="w-6 h-6" />}
            title="Full packing"
            desc="Pros pack, pad, and label everything — fragile-rated boxes included."
            image={PACKING_IMG}
          />
          <ServiceCard
            icon={<Boxes className="w-6 h-6" />}
            title="Storage"
            desc="Climate-controlled holds, short or long-term. Drop-off and pickup."
          />
          <ServiceCard
            icon={<ShieldCheck className="w-6 h-6" />}
            title="Insurance"
            desc="Full-value protection — not just the 60¢-per-lb minimum."
          />
        </div>
      </div>
    </section>
  );
}

function ServiceCard({ icon, title, desc, className = "", image }) {
  return (
    <div
      className={`relative border-2 border-movewave-ink p-7 min-h-[220px] flex flex-col justify-between overflow-hidden ${
        image ? "text-white" : "bg-white"
      } ${className}`}
    >
      {image && (
        <>
          <img src={image} alt="" className="absolute inset-0 w-full h-full object-cover" />
          <div className="absolute inset-0 bg-movewave-ink/65" />
        </>
      )}
      <div className="relative">
        <div className={`w-10 h-10 flex items-center justify-center mb-4 ${image ? "bg-movewave-orange" : "bg-movewave-ink text-white"}`}>
          {icon}
        </div>
        <h3 className="font-display font-bold text-2xl tracking-tight mb-2">{title}</h3>
        <p className={image ? "text-white/85" : "text-gray-600"}>{desc}</p>
      </div>
    </div>
  );
}

/* ============== Social proof ============== */
function SocialProof() {
  return (
    <section className="bg-movewave-ink text-white py-20 sm:py-24 border-y-2 border-movewave-ink">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-2 md:grid-cols-4 gap-10">
        {[
          { n: "12.4K+", l: "Moves matched", icon: <Truck className="w-5 h-5" /> },
          { n: "4.9★", l: "Avg customer rating", icon: <Star className="w-5 h-5" /> },
          { n: "$1.8M+", l: "Saved by customers", icon: <Sparkles className="w-5 h-5" /> },
          { n: "<60s", l: "To get matched", icon: <Clock className="w-5 h-5" /> },
        ].map((s, i) => (
          <div key={i} className="border-l-2 border-movewave-orange pl-5">
            <div className="text-movewave-orange mb-2">{s.icon}</div>
            <div className="font-display font-black text-4xl tracking-tight">{s.n}</div>
            <div className="text-xs uppercase tracking-wider text-white/60 mt-1">{s.l}</div>
          </div>
        ))}
      </div>
    </section>
  );
}

/* ============== Testimonials ============== */
function Testimonials() {
  const t = [
    {
      q: "Got 4 quotes within an hour. Saved nearly $900 compared to the first quote I'd gotten.",
      a: "Maya R.",
      where: "Brooklyn → Austin",
    },
    {
      q: "I was dreading the comparison process. VanLines Scout made it stupid simple — and the movers were excellent.",
      a: "David K.",
      where: "Chicago → Denver",
    },
    {
      q: "Filled it out at 9pm, had a confirmed booking by noon the next day. Couldn't recommend more.",
      a: "Priya S.",
      where: "San Diego (local)",
    },
  ];
  return (
    <section id="reviews" className="py-20 sm:py-28">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-start">
          <div className="lg:col-span-4">
            <div className="text-xs uppercase tracking-[0.2em] font-bold text-movewave-blue mb-3">
              Reviews
            </div>
            <h2 className="font-display font-black text-4xl sm:text-5xl tracking-tighter leading-none mb-6">
              People who've actually moved with us.
            </h2>
            <div className="border-2 border-movewave-ink overflow-hidden">
              <img src={COUPLE_IMG} alt="Happy couple in new home" className="w-full h-64 object-cover" />
            </div>
          </div>
          <div className="lg:col-span-8 grid grid-cols-1 md:grid-cols-2 gap-6">
            {t.map((it, i) => (
              <div key={i} className="border-2 border-movewave-ink p-6 bg-white" data-testid={`testimonial-${i}`}>
                <Quote className="w-6 h-6 text-movewave-orange mb-3" />
                <p className="text-lg leading-relaxed mb-5">"{it.q}"</p>
                <div className="border-t-2 border-gray-100 pt-3">
                  <div className="font-bold">{it.a}</div>
                  <div className="text-xs uppercase tracking-wider text-gray-500">{it.where}</div>
                </div>
              </div>
            ))}
            <div className="border-2 border-movewave-blue bg-movewave-blue text-white p-6 flex flex-col justify-between">
              <div className="flex gap-1 mb-3">
                {Array.from({ length: 5 }).map((_, i) => (
                  <Star key={i} className="w-5 h-5 fill-movewave-orange text-movewave-orange" />
                ))}
              </div>
              <div className="font-display font-black text-5xl tracking-tighter">4.9 / 5</div>
              <div className="text-xs uppercase tracking-wider text-white/70 mt-2">
                from 3,200+ verified moves
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ============== FAQ ============== */
function FAQ() {
  const items = [
    {
      q: "Which areas does VanLines Scout cover?",
      a: "We specialize in the East Coast corridor — Northeast to Southeast moves. That includes origin states like NY, NJ, CT, MA, PA, DE, MD, and DC, with destinations in VA, NC, SC, GA, and FL. We can also help with moves entirely within any of these states.",
    },
    {
      q: "Is VanLines Scout really free?",
      a: "Yes — getting quotes is 100% free. We're paid by our network of moving companies when you choose to book with one, so there's no cost or obligation to you.",
    },
    {
      q: "How are movers vetted?",
      a: "Every company in our network is verified for active state/federal licensing (USDOT/MC), insurance, and we monitor customer reviews on an ongoing basis. Movers below 4.0★ are removed.",
    },
    {
      q: "How fast will I hear from movers?",
      a: "Most customers receive their first quote within 15 minutes. All 4 quotes typically arrive the same day.",
    },
    {
      q: "Can I get a quote for a long-distance or interstate move?",
      a: "Absolutely — we specialize in long-distance interstate moves along the Atlantic corridor, especially Northeast → Southeast (NY/NJ/DE/PA → NC/SC/GA/FL).",
    },
    {
      q: "Will my contact info be shared?",
      a: "Your name, email, and phone are shared only with the up-to-4 vetted movers matched to your request. We never sell your data to third parties.",
    },
  ];
  return (
    <section id="faq" className="bg-white border-y-2 border-movewave-ink py-20 sm:py-28">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-xs uppercase tracking-[0.2em] font-bold text-movewave-blue mb-3 text-center">
          FAQ
        </div>
        <h2 className="font-display font-black text-4xl sm:text-5xl tracking-tighter leading-none text-center mb-12">
          Questions, answered.
        </h2>
        <Accordion type="single" collapsible className="space-y-3">
          {items.map((it, i) => (
            <AccordionItem
              key={i}
              value={`item-${i}`}
              className="border-2 border-movewave-ink rounded-none bg-white"
              data-testid={`faq-item-${i}`}
            >
              <AccordionTrigger className="px-5 py-4 text-left font-bold text-lg hover:no-underline">
                {it.q}
              </AccordionTrigger>
              <AccordionContent className="px-5 pb-5 text-gray-700 leading-relaxed">
                {it.a}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      </div>
    </section>
  );
}

/* ============== Final CTA ============== */
function FinalCTA({ onOpenWizard }) {
  return (
    <section className="py-20 sm:py-28">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="relative bg-movewave-blue text-white border-2 border-movewave-ink p-10 sm:p-16 overflow-hidden shadow-[12px_12px_0_#0A0A0A]">
          <img
            src={WAVE_IMG}
            alt=""
            aria-hidden
            className="absolute -right-20 -top-20 w-[420px] opacity-20 pointer-events-none"
          />
          <div className="relative max-w-2xl">
            <div className="text-xs uppercase tracking-[0.2em] font-bold text-movewave-orange mb-4">
              Stop guessing. Start matching.
            </div>
            <h2 className="font-display font-black text-4xl sm:text-6xl tracking-tighter leading-[0.95] mb-6">
              Your next move<br />starts in 60 seconds.
            </h2>
            <p className="text-white/85 text-lg mb-8 max-w-lg leading-relaxed">
              Free, no obligation, no spam. Just real quotes from vetted moving
              companies near you.
            </p>
            <Button
              onClick={onOpenWizard}
              className="bg-movewave-orange hover:bg-movewave-orange-hover text-white font-bold uppercase tracking-wider rounded-none h-14 px-8 text-sm btn-brut"
              data-testid="final-cta-btn"
            >
              Get my free quote
              <ArrowRight className="w-4 h-4 ml-2" />
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
}

/* ============== Footer ============== */
function Footer() {
  return (
    <footer className="bg-movewave-ink text-white py-14">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-8 mb-10">
          <div>
            <div className="flex items-center gap-2 mb-3">
              <div className="w-8 h-8 bg-movewave-orange flex items-center justify-center">
                <Truck className="w-4 h-4 text-white" strokeWidth={2.5} />
              </div>
              <span className="font-display font-black text-2xl tracking-tight">
                VanLines <span className="text-movewave-orange">Scout</span>
              </span>
            </div>
            <p className="text-white/60 max-w-sm">
              The smartest way to find your mover. Free, fast, vetted.
            </p>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-8 text-sm">
            <div>
              <div className="text-xs uppercase tracking-wider text-movewave-orange font-bold mb-3">Product</div>
              <ul className="space-y-2 text-white/70">
                <li><a href="#how">How it works</a></li>
                <li><a href="#services">Services</a></li>
                <li><Link to="/movers" data-testid="footer-movers-link">Movers by state</Link></li>
                <li><a href="#faq">FAQ</a></li>
              </ul>
            </div>
            <div>
              <div className="text-xs uppercase tracking-wider text-movewave-orange font-bold mb-3">Company</div>
              <ul className="space-y-2 text-white/70">
                <li><a href="#">About</a></li>
                <li><a href="#">Partners</a></li>
                <li><a href="#">Contact</a></li>
              </ul>
            </div>
            <div>
              <div className="text-xs uppercase tracking-wider text-movewave-orange font-bold mb-3">For movers</div>
              <ul className="space-y-2 text-white/70">
                <li><Link to="/admin" data-testid="footer-admin-link">Partner login</Link></li>
                <li><a href="#">Join our network</a></li>
              </ul>
            </div>
          </div>
        </div>
        <div className="border-t border-white/15 pt-6 flex flex-col sm:flex-row justify-between gap-3 text-xs text-white/50">
          <div>© {new Date().getFullYear()} VanLines Scout. All rights reserved.</div>
          <div className="flex gap-6">
            <a href="#">Privacy</a><a href="#">Terms</a><a href="#">USDOT info</a>
          </div>
        </div>
      </div>
    </footer>
  );
}
